import { useEffect, useState } from 'react';
import { useCustomer } from '../../../lib/customer';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { PortalContainer, ComingSoon } from '../PortalShell';

type Deal = {
  id: string;
  deal_number: string;
  title: string;
  channel: string;
  status: string;
  value_mxn: number | null;
  expected_close: string | null;
  project_address: string | null;
  project_city: string | null;
  project_type: string | null;
  project_specifier_name: string | null;
  project_specifier_firm: string | null;
};

export function PortalProyectos() {
  const { customer } = useCustomer();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customer) return;
    supabase
      .from('deals')
      .select('id, deal_number, title, channel, status, value_mxn, expected_close, project_address, project_city, project_type, project_specifier_name, project_specifier_firm')
      .eq('customer_id', customer.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setDeals((data as Deal[]) ?? []);
        setLoading(false);
      });
  }, [customer]);

  if (!customer) return null;

  return (
    <PortalContainer title="Mis proyectos" eyebrow="Project registration · Lock de margen">
      <div style={{
        background: theme.bgFooter,
        border: `1px solid ${theme.border}`,
        borderLeft: `4px solid ${theme.accent}`,
        padding: 20,
        marginBottom: 24,
        display: 'flex',
        gap: 24,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{
            fontSize: 11,
            color: theme.accentDark,
            textTransform: 'uppercase',
            letterSpacing: 1.5,
            fontWeight: 700,
          }}>
            Cómo funciona
          </div>
          <h3 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 500, color: theme.brand, margin: '6px 0 0' }}>
            Registra tu proyecto. Te respetamos el margen.
          </h3>
          <p style={{ fontSize: 14, color: theme.textMuted, lineHeight: 1.6, margin: '8px 0 0' }}>
            Cuando especificas NULED + Lutron en un proyecto, regístralo aquí.
            Si tu cliente nos contacta directo, lo redirigimos contigo. Tu margen
            queda protegido por 90 días desde el registro.
          </p>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 20, color: theme.textMuted }}>Cargando…</div>
      ) : deals.length === 0 ? (
        <div style={{
          padding: 40,
          background: theme.bg,
          border: `1px dashed ${theme.border}`,
          textAlign: 'center',
          color: theme.textMuted,
        }}>
          Aún no tienes proyectos registrados.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {deals.map(d => (
            <article key={d.id} style={{
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              borderLeft: `4px solid ${
                d.status === 'open' ? theme.accent
                : d.status === 'won' ? theme.accentDark
                : theme.textMuted
              }`,
              padding: 20,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 24,
            }}>
              <div>
                <div style={{
                  fontSize: 11,
                  color: theme.accentDark,
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                }}>
                  {d.status} · {d.channel}
                </div>
                <h3 style={{ fontFamily: theme.font.head, fontSize: 18, fontWeight: 600, color: theme.brand, margin: '4px 0' }}>
                  {d.title}
                </h3>
                <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: theme.font.mono }}>
                  {d.deal_number}
                </div>
              </div>
              <div style={{ fontSize: 13, color: theme.textMuted }}>
                {d.project_city && <div>📍 {d.project_city}</div>}
                {d.project_type && <div>{d.project_type}</div>}
                {d.project_specifier_name && (
                  <div>👤 {d.project_specifier_name}{d.project_specifier_firm ? ` · ${d.project_specifier_firm}` : ''}</div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                {d.value_mxn != null && (
                  <div style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 600, color: theme.brand }}>
                    ${Number(d.value_mxn).toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN
                  </div>
                )}
                {d.expected_close && (
                  <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>
                    Cierre estimado: {new Date(d.expected_close).toLocaleDateString('es-MX')}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      <div style={{ marginTop: 32 }}>
        <ComingSoon what="Formulario de Project Registration" />
      </div>
    </PortalContainer>
  );
}
